/** Shape of the detail payload carried by every 'api-error' event. */
export interface ApiErrorDetail {
  /** Human-readable summary (title / message / HTTP fallback). */
  message: string;
  /** Individual field-level validation messages extracted from data.errors. */
  fieldErrors: string[];
  /** HTTP status code. */
  status: number;
}

/**
 * Dispatches a global 'api-error' window event so the ApiErrorSnackbar
 * (mounted once in App.tsx) can display it without any prop drilling.
 */
export const dispatchApiError = (detail: ApiErrorDetail): void => {
  window.dispatchEvent(new CustomEvent<ApiErrorDetail>('api-error', { detail }));
};

/**
 * Extracts a flat array of field-level validation messages from any common
 * API error response shape:
 *
 *   ProblemDetails (ASP.NET)  → { errors: { Field: ["msg", ...] } }
 *   Flat errors array         → { errors: ["msg1", "msg2"] }
 *   Singular error string     → { error: "msg" }
 *   Singular error array      → { error: ["msg1", "msg2"] }
 *   Data is a plain array     → ["msg1", "msg2"]
 */
export const extractFieldErrors = (data: any): string[] => {
  if (!data) return [];

  // data itself is a plain array of strings: ["error1", "error2"]
  if (Array.isArray(data)) {
    return data.filter((m): m is string => typeof m === 'string');
  }

  // data.errors is a keyed object (ProblemDetails): { Field: ["msg"] }
  if (data.errors && typeof data.errors === 'object' && !Array.isArray(data.errors)) {
    return Object.values(data.errors as Record<string, string[]>)
      .flat()
      .filter((m): m is string => typeof m === 'string');
  }

  // data.errors is a flat array: { errors: ["msg1", "msg2"] }
  if (Array.isArray(data.errors)) {
    return data.errors.filter((m: any): m is string => typeof m === 'string');
  }

  // data.error (singular) as array: { error: ["msg1", "msg2"] }
  if (Array.isArray(data.error)) {
    return data.error.filter((m: any): m is string => typeof m === 'string');
  }

  // data.error as a string is handled by extractMessage — don't duplicate here
  return [];
};

/**
 * Safely extracts a "status - message" string from an Axios error caught by
 * call sites that use raw `axios` (bypassing the shared interceptor), so
 * error state is never set to a raw object (which crashes React rendering)
 * and always carries the HTTP status code.
 */
export const getErrorMessage = (error: any, fallback = 'Something went wrong. Please try again.'): string => {
  const status = error?.response?.status;
  const data = error?.response?.data;

  const message = typeof data === 'string'
    ? data
    : data?.title || data?.message || data?.Message || data?.error || error?.message || fallback;

  return status ? `${status} - ${message}` : message;
};
